import { FC, useState, useEffect } from 'react';

import { useMutation, useQueryClient } from 'react-query';
import { useForm, Controller } from 'react-hook-form';

import { IconButton, DialogTitle, DialogContent, TextField, FormHelperText, GlobalStyles } from '@mui/material';
import Button from '@mui/lab/LoadingButton';
import CloseIcon from '@mui/icons-material/Close';
import { updateBundle } from 'api/bundles';
import { useParams } from 'react-router-dom';

type Props = {
    close: () => void;
    field: string;
    contract: any;
}

const ContractEditFieldDialog: FC<Props> = ({ field, contract, ...props }) => {
    const queryClient = useQueryClient();
    const { playerId } = useParams();
    const [blinkKey, setBlinkKey] = useState(0);

    function defaultContractValue() {
        switch (field) {
            case 'personal_share':
                return contract.personal_share;
            case 'payout':
                return contract.payout;
            case 'fix_rate':
                return contract.fix_rate;
            default:
                return '';
        }
    }

    const { control, handleSubmit, formState: { errors, submitCount } } = useForm({
        defaultValues: {
            value: defaultContractValue(),
        },
    });

    useEffect(() => {
        if (submitCount > 0 && errors.value) {
            setBlinkKey(k => k + 1);
        }
    }, [submitCount]);

    const bundleMutation = useMutation(updateBundle, {
        onSuccess: () => {
            queryClient.invalidateQueries(['player-contracts', playerId]);
            props.close();
        },
    });

    const onSubmit = handleSubmit(data => {
        bundleMutation.mutate({ id: contract.id, [field]: data.value });
    });

    function getLabel() {
        switch (field) {
            case 'personal_share':
                return 'доли игрока в текущем пакете';
            case 'payout':
                return 'процента игрока в текущем пакете';
            case 'fix_rate':
                return 'Fix значения в текущем пакете';
            default:
                return '';
        }
    }

    return (
        <>
            <GlobalStyles styles={{
                '@keyframes fixRateBlink': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0 },
                },
            }} />
            <DialogTitle>
                {`Редактирование ${getLabel()}`}
                <IconButton
                    aria-label='close'
                    onClick={() => props.close()}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: theme => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <form className='flex flex-col gap-4 pt-2' onSubmit={onSubmit} noValidate>
                    <Controller
                        control={control}
                        name='value'
                        rules={field === 'fix_rate' ? {
                            validate: v => (Number(v) >= 50 && Number(v) <= 65),
                        } : {}}
                        render={({ field: controllerField }) => (
                            <>
                                <TextField
                                    {...controllerField}
                                    label="Новое значение"
                                    fullWidth
                                    type="number"
                                    inputProps={field === 'fix_rate' ? { step: 1 } : {}}
                                    error={!!errors.value}
                                />
                                {field === 'fix_rate' && (
                                    <FormHelperText
                                        key={blinkKey}
                                        error={!!errors.value}
                                        sx={{
                                            animation: blinkKey > 0 ? 'fixRateBlink 0.35s ease 3' : 'none',
                                        }}
                                    >
                                        Рекомендуемый диапазон: 50–65%
                                    </FormHelperText>
                                )}
                            </>
                        )}
                    />

                    <div className='pt-4'>
                        <Button
                            fullWidth
                            variant='contained'
                            size='large'
                            type='submit'
                            loading={bundleMutation.isLoading}
                        >
                            Редактировать
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </>
    );
};

export default ContractEditFieldDialog;
